import { useCallback, useEffect, useRef, useState } from 'react';

import { QueryHookOptions, useApolloClient } from '@apollo/client';
import { useStoreContext } from 'features/store';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import {
  ILoyaltyUserOfferItemsQuery,
  LoyaltyOfferRedemptionType,
  LoyaltyPaymentMethod,
  LoyaltyPromotionType,
  LoyaltyServiceMode,
  LoyaltyUserOfferItemsDocument,
  useLoyaltyOfferItemsLazyQuery,
  useLoyaltyUserOfferItemsQuery,
} from 'generated/graphql-gateway';
import { useLoyaltySystemwideOffersByIdsQuery } from 'generated/sanity-graphql';
import { useLocaleSmartBlockContent } from 'hooks/use-locale-smart-block-content/use-locale-smart-block-content';
import { groqQuery } from 'remote/api/groq';
import { DISCOUNT_OFFER_CART_ID } from 'state/cart/constants';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { parseOffers } from 'state/global-state/models/loyalty/offers/offers.utils';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useNetworkContext } from 'state/network';
import { getServiceModeFromLS } from 'state/service-mode';
import { toError } from 'utils/errors';
import logger from 'utils/logger';

import {
  AppPersonalizedOffer,
  AppPersonalizedOfferGroup,
  EngineNonPersonalizedOfferListItem,
  EngineOfferListItem,
  EnginePersonalizedOffer,
  LoyaltyAppliedOffer,
  LoyaltyOffer,
  SanityOfferListItemWithExtras,
  isEngineNonPersonalizedOfferListItem,
  isEnginePersonalizedOffer,
  isEnginePersonalizedOfferGroup,
} from '../types';
import { dedupeByIdPreservingOrder, upsertSanityOfferLists } from '../utils';

import { SORTED_LOYALTY_OFFERS_GROQ } from './sortedLoyaltyOffersQuery';
import { IAppliedRewards, IEntriesIdsMap, IQueryLoyaltyUserOffersOptions } from './types';
import { useLoyaltyIncentivesEvaluation } from './use-loyalty-incentive-evaluation';
import { useLoyaltyUserId } from './use-loyalty-user';
import { usePersonalizedOffers } from './use-personalized-offers';
import {
  flattenEntriesToMap,
  normalizeCartEntriesForOfferEvaluation,
  parseEntry,
} from './utils/incentive-evaluation';
import { processPersonalizedOfferGroup } from './utils/personalized-offer-groups';
import { IPersonalizedData, mergePersonalizedData } from './utils/personalized-offers';

interface IUseLoyaltyOffersOptions {
  legacyOfferQueriesEnabled: boolean;
}

// How many times to retry fetching a discount offer's engine/incentive data
// after a transient failure before giving up for this (loyaltyId, ids) pair.
const DISCOUNT_OFFER_QUERY_MAX_ATTEMPTS = 3;
const DISCOUNT_OFFER_QUERY_RETRY_DELAY_MS = 3000;

/** @deprecated useLoyaltyContext instead as this hook has side effects and should only be mounted once */
export const useLoyaltyOffers = ({ legacyOfferQueriesEnabled }: IUseLoyaltyOffersOptions) => {
  // This flag is parent to the next ones
  const loyaltyOffersEnabled = Boolean(useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_OFFERS));
  // This flag replaces Offers 3.0
  const loyaltyStandardOffersEnabled = Boolean(
    useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_STANDARD_OFFERS)
  );

  const enableLoyaltyStandardOffers = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_STANDARD_OFFERS);
  const offersCooldownEnabled = Boolean(useFlag(LaunchDarklyFlag.ENABLE_GLOBAL_OFFERS_COOLDOWN));
  const client = useApolloClient();
  const [evaluating, setEvaluating] = useState(false);
  const dispatch = useAppDispatch();
  const { loyaltyUserId } = useLoyaltyUserId();
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const engineSystemwideOffers = useAppSelector(selectors.loyalty.selectEngineOfferListItems);
  const { store } = useStoreContext();
  const legacyOfferQueriesEnabledRef = useRef(legacyOfferQueriesEnabled);
  legacyOfferQueriesEnabledRef.current = legacyOfferQueriesEnabled;
  const sortedOffersRef = useRef<(LoyaltyOffer | AppPersonalizedOffer)[]>([]);
  const hasFetchedCmsOffersRef = useRef(false);
  const isFetchingCmsOffersRef = useRef(false);
  const cartEntriesIdsMap = useRef<IEntriesIdsMap>({});
  const { language } = useLocale();

  const [
    fetchPersonalizedOfferData,
    { loading: personalizedDataLoading },
  ] = usePersonalizedOffers();
  const storeId = store?.number;

  const { transformSmartBlockContent } = useLocaleSmartBlockContent();
  const { sanityEndpoints } = useNetworkContext();
  const [cmsOffersLoading, setCmsOffersLoading] = useState(false);

  const queryFeatureSortedLoyaltyOffers = useCallback(async () => {
    if (!legacyOfferQueriesEnabledRef.current || isFetchingCmsOffersRef.current) {
      return;
    }
    isFetchingCmsOffersRef.current = true;
    setCmsOffersLoading(true);
    try {
      const data = await groqQuery<
        {
          autoEnabledSystemwideOffers?: SanityOfferListItemWithExtras[] | null;
          sortedSystemwideOffers?: SanityOfferListItemWithExtras[] | null;
        },
        { language: string }
      >(
        sanityEndpoints.groq,
        SORTED_LOYALTY_OFFERS_GROQ,
        { language },
        { loggingTag: 'sortedSystemwideOffers' }
      );

      if (!legacyOfferQueriesEnabledRef.current) {
        return;
      }

      const mergedSystemwideOffers = dedupeByIdPreservingOrder([
        ...(data?.sortedSystemwideOffers ?? []),
        ...(data?.autoEnabledSystemwideOffers ?? []),
      ]);

      if (mergedSystemwideOffers.length) {
        sortedOffersRef.current = upsertSanityOfferLists(
          sortedOffersRef.current,
          mergedSystemwideOffers as LoyaltyOffer[]
        );
      }
      hasFetchedCmsOffersRef.current = true;
    } catch (error) {
      logger.error({ message: 'groq fetch sortedSystemwideOffers error', error });
    } finally {
      isFetchingCmsOffersRef.current = false;
      setCmsOffersLoading(false);
    }
  }, [sanityEndpoints.groq, language]);

  const refetchCmsOffers = useCallback(async () => {
    if (!legacyOfferQueriesEnabled || !legacyOfferQueriesEnabledRef.current) {
      return;
    }
    await queryFeatureSortedLoyaltyOffers();
  }, [legacyOfferQueriesEnabled, queryFeatureSortedLoyaltyOffers]);

  const getPersonalizedOffer = useCallback(
    (engineOffer: EnginePersonalizedOffer, personalizedOfferData: IPersonalizedData) => {
      let personalizedOffer: AppPersonalizedOffer | null = null;

      try {
        personalizedOffer = mergePersonalizedData({
          engineOffer,
          transformSmartBlockContent,
          ...personalizedOfferData,
        });
      } catch (error) {
        const err = toError(error);
        const message = err.message ? `Error: ${err.message}` : '';
        logger.error(`Failed processing personalized offer: ${engineOffer.id} - ${message}`);
      }

      return personalizedOffer;
    },
    [transformSmartBlockContent]
  );

  const onUserOffersQueryComplete = useCallback(
    // `isStale` lets a superseded caller (e.g. the applied-discount-offer
    // effect below, when the offer changes while a request/retry is still in
    // flight) skip dispatching its response — the dispatches below REPLACE
    // (not merge) engine/personalized offer state, so an out-of-order stale
    // response could otherwise clobber a newer, correct one. Re-checked after
    // `fetchPersonalizedOfferData`'s own await, not just on entry, since
    // staleness can develop during that gap too. See BKPE-28076 followup.
    async (data: ILoyaltyUserOfferItemsQuery, isStale?: () => boolean) => {
      if (!legacyOfferQueriesEnabledRef.current || isStale?.()) {
        return;
      }

      const loyaltyUser = data?.loyaltyUserV2;
      const offerRedemptionAvailableAfter =
        loyaltyUser?.offerRedemptionAvailability?.availableAfter;

      if (offerRedemptionAvailableAfter && offersCooldownEnabled) {
        dispatch(actions.loyalty.setOfferRedemptionAvailableAfter(offerRedemptionAvailableAfter));
      }

      const engineUserOfferItems = (loyaltyUser?.offerItems || []) as EngineOfferListItem[];
      if (!engineUserOfferItems.length) {
        return;
      }

      // Fetch data needed to build personalized offer
      const personalizedOfferData = await fetchPersonalizedOfferData(engineUserOfferItems);

      if (!legacyOfferQueriesEnabledRef.current || isStale?.()) {
        return;
      }

      const appPersonalizedOffers: AppPersonalizedOffer[] = [];
      const appPersonalizedOfferGroups: AppPersonalizedOfferGroup[] = [];
      const engineOfferListItems: EngineNonPersonalizedOfferListItem[] = [];

      engineUserOfferItems.forEach(item => {
        if (isEnginePersonalizedOffer(item) && personalizedOfferData) {
          const personalizedOffer = getPersonalizedOffer(item, personalizedOfferData);
          if (personalizedOffer) {
            appPersonalizedOffers.push(personalizedOffer);
          }
        }

        if (isEnginePersonalizedOfferGroup(item) && personalizedOfferData) {
          const group = processPersonalizedOfferGroup(
            item,
            personalizedOfferData.systemwideOffersMap,
            personalizedOfferData.offerGroupsMap
          );
          if (group) {
            appPersonalizedOfferGroups.push(group);
          }
        }

        if (isEngineNonPersonalizedOfferListItem(item)) {
          engineOfferListItems.push(item);
        }
      });

      sortedOffersRef.current = sortedOffersRef.current.concat(appPersonalizedOffers);

      dispatch(actions.loyalty.setCmsOffers(sortedOffersRef.current));
      dispatch(actions.loyalty.setEngineOfferListItems(engineOfferListItems));
      dispatch(actions.loyalty.setAppPersonalizedOffers(appPersonalizedOffers));
      dispatch(actions.loyalty.setAppPersonalizedOfferGroups(appPersonalizedOfferGroups));
    },
    [dispatch, fetchPersonalizedOfferData, getPersonalizedOffer, offersCooldownEnabled]
  );

  const [queryEngineOffers, { loading: engineOffersLoading }] = useLoyaltyOfferItemsLazyQuery({
    fetchPolicy: 'network-only',
    variables: {
      where: {
        storeId,
        omitInvalids: false,
        includeOfferGroups: true,
      },
    },
    onCompleted: data => {
      if (legacyOfferQueriesEnabledRef.current && data?.loyaltyOfferItems?.length) {
        dispatch(actions.loyalty.setCmsOffers(sortedOffersRef.current));
        dispatch(
          actions.loyalty.setGuestEngineOfferListItems(
            data.loyaltyOfferItems as EngineOfferListItem[]
          )
        );
      }
    },
  });

  // Attach applied offers ids to get the full collection of offers from CMS
  const appliedLoyaltyOffersIds = appliedOffers.map(({ cmsId }) => cmsId || '');

  // Tracks which applied-offer ids this reactive query's LAST successful
  // response actually covered — `engineSystemwideOffers.length > 0` (part of
  // this query's own `skip`) only tells us it succeeded at SOME point, not
  // that it covered the CURRENT discount offer specifically (it self-disables
  // after any success, so a discount offer applied afterward would never be
  // picked up by it again). The applied-discount-offer fallback effect below
  // uses this to distinguish "already covered, don't duplicate" from
  // "not covered, still need to fetch" instead of inferring it from
  // `engineSystemwideOffers`. See BKPE-28076 followup.
  const appliedOffersQueryLastCoveredIdsRef = useRef<Set<string>>(new Set());

  const { loading: queryUserAppliedOffersLoading } = useLoyaltyUserOfferItemsQuery({
    fetchPolicy: 'no-cache',
    skip:
      !legacyOfferQueriesEnabled ||
      !loyaltyUserId ||
      !appliedLoyaltyOffersIds.length ||
      engineSystemwideOffers.length > 0,
    variables: {
      loyaltyId: loyaltyUserId,
      where: {
        ids: appliedOffers.map(({ id }) => id || ''),
        includeOfferGroups: true,
      },
    },
    onCompleted: data => {
      appliedOffersQueryLastCoveredIdsRef.current = new Set(
        (data.loyaltyUserV2?.offerItems || []).flatMap(({ id }) => (id ? [id] : []))
      );
      onUserOffersQueryComplete(data);
    },
  });

  const { loading: swoffersLoading } = useLoyaltySystemwideOffersByIdsQuery({
    variables: {
      ids: appliedLoyaltyOffersIds,
    },
    skip: !legacyOfferQueriesEnabled || !appliedLoyaltyOffersIds.length,
    onCompleted(data) {
      if (legacyOfferQueriesEnabledRef.current && data?.allSystemwideOffers) {
        sortedOffersRef.current = sortedOffersRef.current.concat(
          data.allSystemwideOffers as LoyaltyOffer[]
        );

        dispatch(actions.loyalty.setCmsOffers(sortedOffersRef.current));
      }
    },
  });

  const [queryUserOffersLoading, setQueryUserOffersLoading] = useState(false);
  // Returns whether the request completed successfully, so callers that need
  // to retry a transient failure (see the applied-discount-offer effect
  // below) don't have to rely on this swallowing its own errors silently.
  const queryUserOffers = useCallback(
    async ({
      variables,
      isStale,
      ...options
    }: QueryHookOptions<any, any> & { isStale?: () => boolean }): Promise<boolean> => {
      if (!legacyOfferQueriesEnabledRef.current) {
        return false;
      }

      const { where, ...otherVariables } = variables || {};
      setQueryUserOffersLoading(true);
      dispatch(actions.loyalty.setOffersLoading(true));

      try {
        const { data } = await client.query<ILoyaltyUserOfferItemsQuery>({
          ...options,
          fetchPolicy: 'no-cache',
          query: LoyaltyUserOfferItemsDocument,
          variables: {
            ...otherVariables,
            where: {
              ...where,
              includeOfferGroups: true,
            },
          },
        });

        if (legacyOfferQueriesEnabledRef.current && data && !isStale?.()) {
          // Awaited so this only resolves once the (possibly slower)
          // personalized-offer transformation/dispatch has actually
          // finished — callers use this to know when it's safe to stop
          // treating the fetch as "in flight" (e.g. the applied-discount-
          // offer effect's pending/loading gate below). Returning early
          // here let `onUserOffersQueryComplete`'s own async work outlive
          // the caller's notion of "done," which could let `OrderProvider`
          // treat the offer as settled before its CMS data actually landed.
          await onUserOffersQueryComplete(data, isStale);
        }
        return true;
      } catch (e) {
        logger.error(`Error fetching user offers: ${e}`);
        return false;
      } finally {
        setQueryUserOffersLoading(false);
        dispatch(actions.loyalty.setOffersLoading(false));
      }
    },
    [client, dispatch, onUserOffersQueryComplete]
  );

  // Fetches full engine/incentive data for applied discount (upsize) offers.
  // Decoupled from `useLoyaltySystemwideOffersByIdsQuery`'s `onCompleted`
  // (a one-shot event) so it also re-fires if `loyaltyUserId` resolves AFTER
  // that query has already completed on a fresh reload — otherwise this data
  // never loads at all for that session. See BKPE-28076 followup.
  const queriedDiscountOfferIdsKeyRef = useRef<string | null>(null);
  // `queryUserOffers` clears its own loading flag between retry attempts
  // (see `DISCOUNT_OFFER_QUERY_RETRY_DELAY_MS`), which would otherwise let
  // `offersLoading` read `false` during that gap — and, in `OrderProvider`,
  // that reads as "settled," letting its eviction debounce remove this
  // offer before the retry has a chance to restore its CMS data. Keeping
  // this `true` for the whole retry chain (not just each individual
  // attempt) prevents that. See BKPE-28076 followup.
  const [isDiscountOfferQueryPending, setIsDiscountOfferQueryPending] = useState(false);
  useEffect(() => {
    if (!legacyOfferQueriesEnabled || !loyaltyUserId) {
      return undefined;
    }

    // `useLoyaltyUserOfferItemsQuery` above already fetches ALL applied
    // offer ids (a superset including this discount offer's id) whenever
    // it isn't skipped. Firing this fallback while it's actively fetching
    // would duplicate that network request, so defer entirely while it's
    // in flight.
    const isAppliedOffersQueryActive =
      appliedLoyaltyOffersIds.length > 0 && engineSystemwideOffers.length === 0;
    if (isAppliedOffersQueryActive) {
      return undefined;
    }

    const upsizeAndDiscountIds = appliedOffers
      .filter(({ cartId }) => cartId === DISCOUNT_OFFER_CART_ID)
      .map(({ id }) => id || '');

    if (!upsizeAndDiscountIds.length) {
      return undefined;
    }

    // That reactive query self-disables after ANY success (its own `skip`
    // includes `engineSystemwideOffers.length > 0`), so `!isAppliedOffersQueryActive`
    // above is also true right after it just succeeded for something
    // entirely unrelated — NOT only when this specific discount offer was
    // already covered. `onUserOffersQueryComplete`'s dispatches REPLACE (not
    // merge) engine/personalized offer state, so firing this fallback in
    // that case would clobber another applied offer's data the reactive
    // query just correctly set. Only treat this offer as already covered if
    // its id was actually part of that query's last successful response.
    const isCoveredByAppliedOffersQuery = upsizeAndDiscountIds.every(id =>
      appliedOffersQueryLastCoveredIdsRef.current.has(id)
    );
    if (isCoveredByAppliedOffersQuery) {
      return undefined;
    }

    // Fetch ALL currently-applied offer ids here, not just the discount
    // subset: `onUserOffersQueryComplete`'s dispatches REPLACE (not merge)
    // engine/personalized offer state, so a discount-only response would
    // wipe out any other applied offer's engine/personalized data that was
    // already loaded (e.g. by the reactive query above, before it
    // self-disabled) — and since that reactive query stays permanently
    // skipped once `engineSystemwideOffers` is non-empty, that data could
    // never be reloaded afterward. Still uses `omitInvalids: false`, since
    // that's this fallback's whole reason to exist. See BKPE-28076 followup.
    const allAppliedOfferIds = appliedOffers.map(({ id }) => id || '');

    // `queryUserOffers` is an imperative fetch, not a reactive query — guard
    // against re-firing for the same (loyaltyId, ids) pair on every
    // unrelated re-render (e.g. `appliedOffers` identity churn). Set
    // synchronously (not just once the request settles) so a near-simultaneous
    // second effect invocation for the same pair doesn't start a parallel
    // fetch/retry chain.
    const queryKey = `${loyaltyUserId}:${allAppliedOfferIds.slice().sort().join(',')}`;
    if (queriedDiscountOfferIdsKeyRef.current === queryKey) {
      return undefined;
    }
    queriedDiscountOfferIdsKeyRef.current = queryKey;

    let cancelled = false;
    // Whether this chain reached a terminal state (succeeded, or exhausted
    // its retries) on its own, as opposed to being torn down mid-flight by
    // the effect's cleanup (e.g. an unrelated dependency change while a
    // request/retry wait was still in progress).
    let concluded = false;
    let retryTimeoutId: ReturnType<typeof setTimeout> | undefined;

    // `queryUserOffers` can fail transiently (network blip, etc.) — retry a
    // bounded number of times before giving up, so a transient failure
    // doesn't silently and permanently block this offer's data (and,
    // downstream, minimal-cart repricing in `use-minimal-cart.ts`) for the
    // rest of the session.
    setIsDiscountOfferQueryPending(true);
    const attemptFetch = (attempt: number) => {
      queryUserOffers({
        variables: {
          loyaltyId: loyaltyUserId,
          where: {
            omitInvalids: false,
            ids: allAppliedOfferIds,
          },
        },
        // If this chain is torn down (e.g. the offer changes) while this
        // specific request is in flight, its response must not be allowed to
        // dispatch — see `onUserOffersQueryComplete`/`queryUserOffers` above.
        isStale: () => cancelled,
      }).then(success => {
        if (cancelled) {
          return;
        }
        if (success) {
          // Record coverage symmetrically with the reactive query's own
          // `onCompleted`, so a later discount-offer effect run (or the
          // reactive query itself, if it somehow re-evaluates) can tell this
          // set of ids was already handled.
          appliedOffersQueryLastCoveredIdsRef.current = new Set(allAppliedOfferIds);
        }
        if (success || attempt >= DISCOUNT_OFFER_QUERY_MAX_ATTEMPTS) {
          concluded = true;
          setIsDiscountOfferQueryPending(false);
          return;
        }
        retryTimeoutId = setTimeout(
          () => attemptFetch(attempt + 1),
          DISCOUNT_OFFER_QUERY_RETRY_DELAY_MS
        );
      });
    };

    attemptFetch(1);

    return () => {
      cancelled = true;
      if (retryTimeoutId) {
        clearTimeout(retryTimeoutId);
      }
      setIsDiscountOfferQueryPending(false);
      // If this chain never reached a terminal state (e.g. torn down by an
      // unrelated dependency change while a request/retry wait was still in
      // flight), clear the dedup key so a replacement effect for the same
      // (loyaltyId, ids) pair can restart the fetch — otherwise the guard
      // above would see the key still set and skip forever, leaving this
      // offer's data permanently unresolved.
      if (!concluded && queriedDiscountOfferIdsKeyRef.current === queryKey) {
        queriedDiscountOfferIdsKeyRef.current = null;
      }
    };
  }, [
    appliedOffers,
    appliedLoyaltyOffersIds.length,
    engineSystemwideOffers.length,
    legacyOfferQueriesEnabled,
    loyaltyUserId,
    queryUserOffers,
  ]);

  const queryLoyaltyUserOffers = useCallback(
    ({
      redemptionTypes,
      omitInvalids = false,
    }: {
      redemptionTypes: LoyaltyOfferRedemptionType[];
      omitInvalids?: boolean;
    }) => ({
      loyaltyId,
      cartEntries,
      appliedRewards,
      appliedLoyaltyOffers = [],
    }: IQueryLoyaltyUserOffersOptions) => {
      if (!legacyOfferQueriesEnabled || !legacyOfferQueriesEnabledRef.current) {
        return;
      }

      const normalizedCartEntries = normalizeCartEntriesForOfferEvaluation(
        cartEntries,
        appliedLoyaltyOffers
      );

      const parsedEntries = normalizedCartEntries?.reduce(
        parseEntry({
          type: LoyaltyPromotionType.OFFER,
          incentivesToExclude: appliedRewards,
        }),
        []
      );

      cartEntriesIdsMap.current = (parsedEntries || []).reduce(flattenEntriesToMap, {});

      return queryUserOffers({
        variables: {
          loyaltyId,
          where: {
            appliedIncentives: parseOffers(appliedLoyaltyOffers),
            cartEntries: parsedEntries,
            serviceMode: getServiceModeFromLS() || undefined,
            redemptionTypes,
            storeId,
            omitInvalids,
          },
        },
      });
    },
    [legacyOfferQueriesEnabled, queryUserOffers, storeId]
  );

  // Offers without loyaltyId should always be of redemption type STANDARD
  const queryLoyaltyOffers = useCallback(() => {
    if (!legacyOfferQueriesEnabled || !legacyOfferQueriesEnabledRef.current) {
      return;
    }

    const sm = getServiceModeFromLS();
    const lsm = Object.entries(LoyaltyServiceMode).find(([key]) => key === sm)?.[1] || undefined;
    queryEngineOffers({
      variables: {
        where: {
          serviceMode: lsm,
          storeId,
          redemptionTypes: [LoyaltyOfferRedemptionType.STANDARD],
          includeOfferGroups: true,
        },
      },
    });
  }, [legacyOfferQueriesEnabled, queryEngineOffers, storeId]);

  /**
   * Lazy-load sortedSystemwideOffers (CMS only). Call when a screen that needs offers mounts
   * (e.g. Loyalty Offers, Cart). Idempotent after first successful fetch — does not re-trigger
   * engine offer queries; screens run their own fetch flow (e.g. Offers uses refreshOffers/fetchOffers).
   */
  const ensureSortedOffersLoaded = useCallback(async () => {
    const shouldSkipLoad =
      !legacyOfferQueriesEnabled ||
      !enableLoyaltyStandardOffers ||
      hasFetchedCmsOffersRef.current ||
      isFetchingCmsOffersRef.current;
    if (shouldSkipLoad) {
      return;
    }
    await queryFeatureSortedLoyaltyOffers();
    if (!legacyOfferQueriesEnabledRef.current) {
      return;
    }
    queryLoyaltyOffers();
  }, [
    enableLoyaltyStandardOffers,
    legacyOfferQueriesEnabled,
    queryFeatureSortedLoyaltyOffers,
    queryLoyaltyOffers,
  ]);

  const queryLoyaltyUserStandardOffers = useCallback(
    (options: IQueryLoyaltyUserOffersOptions) =>
      queryLoyaltyUserOffers({
        redemptionTypes: [LoyaltyOfferRedemptionType.STANDARD],
      })(options),
    [queryLoyaltyUserOffers]
  );

  const { evaluateLoyaltyIncentives } = useLoyaltyIncentivesEvaluation();

  /**
   * Evaluates applied loyalty offers against cart entries.
   *
   * IMPORTANT: Callers must ensure the unified menu is loaded before calling
   * this function. Use `indexedMenu.isLoaded()` from `useUnifiedMenuContext()`
   * to check. This avoids stale closure issues when the check is done inside
   * this callback.
   *
   * @see YourCart - guards with `isUnifiedMenuLoaded` in useEffect
   * @see useCartAvailabilityCheck - guards with `isUnifiedMenuLoaded` in
   * useEffect
   */
  const evaluateLoyaltyUserOffers = useCallback(
    async (
      loyaltyId: string,
      appliedLoyaltyOffers: LoyaltyAppliedOffer[],
      cartEntries?: ILegacyCartEntry[],
      appliedLoyaltyRewards?: IAppliedRewards | null,
      subtotalAmount?: number,
      paymentMethod?: LoyaltyPaymentMethod | null
    ) => {
      const normalizedCartEntries = normalizeCartEntriesForOfferEvaluation(
        cartEntries,
        appliedLoyaltyOffers
      );

      const parsedEntries = normalizedCartEntries?.reduce(
        parseEntry({
          type: LoyaltyPromotionType.OFFER,
          incentivesToExclude: appliedLoyaltyRewards,
        }),
        []
      );

      cartEntriesIdsMap.current = (parsedEntries || []).reduce(flattenEntriesToMap, {});

      setEvaluating(true);

      try {
        const evaluationResult = await evaluateLoyaltyIncentives({
          loyaltyId,
          appliedIncentives: appliedLoyaltyOffers,
          incentiveType: LoyaltyPromotionType.OFFER,
          cartEntries: parsedEntries,
          subtotalAmount,
          paymentMethod,
          serviceMode: getServiceModeFromLS(),
          storeId,
        });

        if (evaluationResult) {
          dispatch(actions.loyalty.setOffersFeedbackMap(evaluationResult));
        }
      } finally {
        setEvaluating(false);
      }
    },
    [dispatch, evaluateLoyaltyIncentives, storeId]
  );

  useEffect(() => {
    dispatch(
      actions.loyalty.setOffersLoading(
        queryUserOffersLoading ||
          engineOffersLoading ||
          personalizedDataLoading ||
          cmsOffersLoading ||
          swoffersLoading ||
          evaluating ||
          queryUserAppliedOffersLoading ||
          isDiscountOfferQueryPending
      )
    );
  }, [
    dispatch,
    queryUserOffersLoading,
    engineOffersLoading,
    personalizedDataLoading,
    cmsOffersLoading,
    isDiscountOfferQueryPending,
    swoffersLoading,
    evaluating,
    queryUserAppliedOffersLoading,
  ]);

  return {
    evaluateLoyaltyUserOffers,
    ensureSortedOffersLoaded,
    loyaltyOffersEnabled,
    loyaltyStandardOffersEnabled,
    queryLoyaltyOffers,
    queryLoyaltyUserStandardOffers,
    refetchCmsOffers,
    cmsOffersLoading,
  };
};
